import { useRef, useState } from "react";
import type { EvidenciaOut } from "@/lib/api";
import SelectorEvidencias from "@/components/evidencias/SelectorEvidencias";

type Props = {
  /** Para «De Evidencias»: elegir una foto que ya está en el caso sin resubirla. */
  casoId: number;
  fotos: EvidenciaOut[];
  onChange: (fotos: EvidenciaOut[]) => void;
  agregar: (lista: FileList | File[]) => void | Promise<void>;
  subiendo: string | null;
  disabled?: boolean;
};

/** Regla de la bitácora (espejo de `domain/bitacora_fotos`): solo imágenes vivas. */
function elegibleParaNota(ev: EvidenciaOut): { ok: boolean; motivo?: string } {
  if (ev.archivo_eliminado_at) return { ok: false, motivo: "Ya no está en el servidor" };
  if (!ev.mime.startsWith("image/")) return { ok: false, motivo: "Solo fotos en la bitácora" };
  return { ok: true };
}

/**
 * Control de fotos de constancia dentro del compositor de la bitácora.
 *
 * Es un botón compacto que vive en la MISMA fila que «Registrar llamada»: una
 * banda punteada a todo lo ancho partía en dos los controles de «± Cambiar
 * estado» y pesaba más que su importancia. El arrastrar sigue funcionando —y
 * sobre todo el compositor completo, no solo este botón—; el clic es el
 * respaldo.
 *
 * Las miniaturas pendientes se pintan en su propio renglón (`basis-full`
 * dentro del contenedor `flex-wrap` del padre), con sus nombres debajo: antes
 * el nombre vivía en un tooltip. Qué hace cada botón lo dice la Ayuda de la
 * bitácora.
 */
export default function ZonaFotos({
  casoId,
  fotos,
  onChange,
  agregar,
  subiendo,
  disabled,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectorOpen, setSelectorOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => !disabled && inputRef.current?.click()}
        disabled={disabled}
        className="btn-ghost text-sm border border-ink-600 w-full sm:w-auto disabled:opacity-50"
      >
        {subiendo ? "Subiendo foto…" : "📎 Fotos"}
        {fotos.length > 0 && !subiendo && (
          <span className="ml-1 text-accent-300">({fotos.length})</span>
        )}
      </button>
      {/* Una foto que YA está en Evidencias no hay que volver a subirla: se cita.
          El backend siempre lo aceptó (`evidencia_ids` del POST de notas); lo que
          faltaba era la puerta. */}
      <button
        type="button"
        onClick={() => !disabled && setSelectorOpen(true)}
        disabled={disabled}
        className="btn-ghost text-sm border border-ink-600 w-full sm:w-auto disabled:opacity-50"
      >
        🖼️ De Evidencias
      </button>
      {selectorOpen && (
        <SelectorEvidencias
          casoId={casoId}
          onClose={() => setSelectorOpen(false)}
          titulo="Citar una foto de Evidencias"
          descripcion="Elige una foto que ya está en el caso; queda ligada a esta nota sin subirla otra vez. Puedes elegir varias."
          elegible={elegibleParaNota}
          yaElegidas={fotos.map((f) => f.id)}
          multiple
          onElegir={(ev) => onChange([...fotos, ev])}
        />
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files) void agregar(e.target.files);
          e.target.value = "";
        }}
      />

      {fotos.length > 0 && (
        <div className="basis-full flex flex-wrap gap-1.5">
          {fotos.map((f) => (
            <div key={f.id} className="relative">
              <img
                src={`/api/evidencias/${f.id}/file`}
                alt={f.nombre_original}
                loading="lazy"
                className="h-16 w-16 rounded border border-zinc-700 object-cover"
              />
              <button
                type="button"
                onClick={() => onChange(fotos.filter((x) => x.id !== f.id))}
                aria-label={`Quitar ${f.nombre_original} de esta nota`}
                className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-zinc-900 border border-zinc-600 text-zinc-300 text-xs leading-none hover:bg-red-600 hover:border-red-500 hover:text-white transition"
              >
                ×
              </button>
            </div>
          ))}
          <p className="w-full text-[10px] text-zinc-500 break-words [overflow-wrap:anywhere]">
            {fotos.map((f) => f.nombre_original).join(" · ")}
          </p>
        </div>
      )}
    </>
  );
}
